import clsx from 'clsx';
import { useState } from 'react';
import Image from 'components/ui/Image';
import config from 'config';
import { useTranslation } from 'hooks/useTranslation';
import styles from './TreatmentsCard.module.scss';
import type {
  IBlockTreatmentsCard,
  ITreatmentPageFields,
} from 'types/contentful/generated';

type Props = {
  count: number;
  item: IBlockTreatmentsCard;
  speciality: string;
  className?: string;
};

const TreatmentsCard = ({
  count,
  item,
  speciality,
  className,
}: Props): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t, locale } = useTranslation();

  const treatmentCardItem = item.fields || item;

  const treatmentItems =
    // eslint-disable-next-line
    // @ts-ignore
    treatmentCardItem.treatments?.items || treatmentCardItem.treatments;

  const filteredLocaleTreatments =
    treatmentItems?.filter((treatment: ITreatmentPageFields) => {
      // eslint-disable-next-line
      // @ts-ignore
      const { disableLocale } = treatment.fields || treatment;
      return !disableLocale;
    }) || [];

  // -- We show treatments we have in the platform (as links)
  // -- But also text treatments that link no-where but help on content
  const allTreatments = [
    ...(filteredLocaleTreatments || []),
    ...((!treatmentCardItem.disableAdditionalTreatments &&
      treatmentCardItem.additionalTreatments) ||
      []),
  ];

  const { treatmentsCardSlider, treatmentsCardItemCount } = config.defaults;
  const isLongList = count > treatmentsCardSlider.threshold;
  const { image, title } = treatmentCardItem;

  const renderImage = (): JSX.Element | null => {
    if (!image) {
      return null;
    }

    const { description: imageDescription, title: imageTitle } =
      image.fields || image;

    return (
      <div className={styles.imageWrapper}>
        <Image
          className={styles.image}
          // eslint-disable-next-line
          // @ts-ignore
          src={image.fields?.file.url || image.url}
          alt={imageDescription || imageTitle || ''}
          fill
        />
      </div>
    );
  };

  return (
    <div className={clsx(styles.container, className)}>
      {renderImage()}
      <div className={styles.wrapper}>
        <div className={clsx(styles.title)}>{title}</div>
        {allTreatments.map((treatment, i) => {
          // eslint-disable-next-line
          // @ts-ignore
          const isTextEntry = !treatment.fields && !treatment.slug;
          const treatmentItem: ITreatmentPageFields =
            treatment.fields || treatment;

          const { speciality: specialityFields } = treatmentItem;

          let specialitySlug = '';
          if (!isTextEntry && specialityFields) {
            const { slug } = specialityFields.fields || specialityFields;
            specialitySlug = `${slug || speciality}/`;
          }

          const itemClassName = clsx(
            styles.item,
            isLongList ? styles.itemLongList : styles.itemShortList,
            i + 1 > treatmentsCardItemCount && !isExpanded && styles.itemHidden,
            isTextEntry ? styles.itemIsText : styles.itemIsLink,
          );

          return isTextEntry ? (
            <span key={i} className={itemClassName}>
              {treatmentItem.title || treatmentItem}
            </span>
          ) : (
            <a
              key={i}
              className={itemClassName}
              // eslint-disable-next-line react/forbid-dom-props
              href={`/${locale}/${specialitySlug}${treatmentItem.slug}`}
            >
              {treatmentItem.treatmentName || treatmentItem.title}
            </a>
          );
        })}
        {allTreatments.length > treatmentsCardItemCount && (
          <span
            className={styles.expand}
            onClick={() => {
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? t('showLess') : t('showMore')}
          </span>
        )}
      </div>
    </div>
  );
};

export default TreatmentsCard;
